// ============================================================
// ExpressLoggingSetup.jsx
//
// A branded, responsive course content page for the
// "Logging with Morgan & Winston" lesson.
//
// Author: ProDev Linux Series (Observability Branch)
// Features: HTTP request tracking, Winston transport config,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AssessmentIcon         from "@mui/icons-material/Assessment";
import HistoryEduIcon         from "@mui/icons-material/HistoryEdu";
import BugReportIcon          from "@mui/icons-material/BugReport";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import StorageIcon           from "@mui/icons-material/Storage";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressLoggingSetup() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Observability · Module 24"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Server-Side <span>Logging</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Visibility is the backbone of reliability. By combining Morgan for HTTP request tracking and Winston for application-level event logging, you create a professional observability pipeline.
                    </Typography>
                </Box>

                {/* 1. INSTALLATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Core Dependencies" />
                    <CodeBlock lines={[
                        { cmd: "npm install morgan winston", comment: "Install request and system loggers" },
                    ]} />
                </Box>

                {/* 2. MORGAN */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AssessmentIcon />} label="2. HTTP Logging (Morgan)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Morgan is a middleware that automatically logs every incoming request. Use the <code style={{color: BRAND.white}}>'dev'</code> preset for development or custom tokens for production metrics.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const morgan = require('morgan');", comment: "" },
                        { cmd: "app.use(morgan('dev'));", comment: "Standard console output" },
                        { cmd: "", comment: "" },
                        { cmd: "morgan.token('host', (req) => req.hostname);", comment: "Define custom tracking token" },
                        { cmd: "app.use(morgan(':method :url :status - :response-time ms :host'));", comment: "Custom format string" },
                    ]} />
                </Box>

                {/* 3. WINSTON */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<HistoryEduIcon />} label="3. System Logging (Winston)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Winston allows you to send logs to different "transports" (Console, File, or external APIs) and format them with timestamps and levels.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// utils/logger.js", comment: "" },
                        { cmd: "const { createLogger, format, transports } = require('winston');", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "const logger = createLogger({", comment: "" },
                        { cmd: "  level: 'info',", comment: "Default logging level" },
                        { cmd: "  format: format.combine(format.timestamp(), format.json()),", comment: "Format as JSON for production" },
                        { cmd: "  transports: [", comment: "" },
                        { cmd: "    new transports.File({ filename: 'logs/error.log', level: 'error' }),", comment: "Persistent error logs" },
                        { cmd: "    new transports.File({ filename: 'logs/combined.log' })", comment: "All logs combined" },
                        { cmd: "  ]", comment: "" },
                        { cmd: "});", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "module.exports = logger;", comment: "" },
                    ]} />
                </Box>

                {/* 4. INTEGRATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="4. Capturing Events & Errors" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Import your Winston instance into your app to log significant business events or catch-all errors.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const logger = require('./utils/logger');", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.get('/users', (req, res) => {", comment: "" },
                        { cmd: "  logger.info('Users list fetched');", comment: "Manual info log" },
                        { cmd: "  res.send(users);", comment: "" },
                        { cmd: "});", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "app.use((err, req, res, next) => {", comment: "Global Error Handler" },
                        { cmd: "  logger.error(`${err.status || 500} - ${err.message}`);", comment: "Log errors to file" },
                        { cmd: "  res.sendStatus(500);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="Best Practices" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Log Rotation", desc: "Use winston-daily-rotate-file to prevent log files from eating up your disk space.", icon: <CheckCircleOutlineIcon /> },
                            { title: "JSON Formatting", desc: "Always log in JSON format in production to make logs machine-readable for ELK or Datadog.", icon: <CodeIcon /> },
                            { title: "Sensitive Data", desc: "Never log passwords, credit card numbers, or JWT secrets in your log files.", icon: <StarIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Don't forget to add your <strong>logs/</strong> directory to your <code style={{ color: BRAND.goldBright }}>.gitignore</code>! You don't want to push megabytes of local server logs to your GitHub repository.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
